/**
 * Helpers do workspace de projetos (Task 13) — conversões de data, rótulos de
 * modo de dependência, semântica de status/atraso e montagem do modelo do Gantt
 * a partir do `ProjectGanttResponse`.
 *
 * Datas: os campos `startDate`/`endDate` do backend são ISO em UTC-midnight
 * (dia de calendário). Para exibir/editar sem off-by-one de fuso, convertemos
 * sempre pelo DIA UTC (`getUTC*`), nunca pela hora local do `Date`.
 */

import type { SelectOption } from '@/shared/components/ui';
import type {
  DependencyMode,
  ProjectGanttResponse,
  ProjectGanttTask,
  ProjectPhase,
} from '../types';
import type { TroiaGanttLink, TroiaGanttTask } from './gantt/types';

/* ─── Modos de dependência ─────────────────────────────────────────── */

export const MODE_LABEL: Record<DependencyMode, string> = {
  inform: 'Informar',
  warn: 'Avisar',
  block: 'Bloquear',
};

export const MODE_OPTIONS: SelectOption[] = [
  { value: 'inform', label: 'Informar (só notifica ao liberar)' },
  { value: 'warn', label: 'Avisar (confirma antes de avançar)' },
  { value: 'block', label: 'Bloquear (impede avançar antes)' },
];

/** Cores dos chips de modo (tema-agnóstico, rgba sobre superfície do card). */
export const MODE_CHIP: Record<DependencyMode, { bg: string; fg: string }> = {
  inform: { bg: 'rgba(148, 163, 184, 0.18)', fg: '#475569' },
  warn: { bg: 'rgba(245, 158, 11, 0.16)', fg: '#b45309' },
  block: { bg: 'rgba(239, 68, 68, 0.14)', fg: '#b91c1c' },
};

/* ─── Datas ────────────────────────────────────────────────────────── */

/** ISO (UTC-midnight) → `yyyy-mm-dd` para `<input type="date">`. */
export function isoToDateInput(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** `yyyy-mm-dd` → ISO em UTC-midnight (contrato do backend). */
export function dateInputToIso(value: string): string {
  if (!value) return new Date().toISOString();
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return Number.isNaN(parsed.getTime()) ? new Date().toISOString() : parsed.toISOString();
}

/** ISO (UTC-midnight) → `Date` de meia-noite LOCAL do mesmo dia (para o Gantt). */
export function isoToLocalDate(iso: string): Date {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return new Date();
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/** `Date` local → ISO UTC-midnight do mesmo dia de calendário. */
export function localDateToIso(date: Date): string {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString();
}

/** ISO → `dd/mm/yyyy` (dia de calendário UTC, sem shift de fuso). */
export function fmtDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${d.getUTCFullYear()}`;
}

/** ISO → `dd/mm` curto. */
export function fmtShort(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${day}/${month}`;
}

/** Dia de calendário UTC de hoje (para comparações de atraso). */
function todayUtcMs(): number {
  const now = new Date();
  return Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
}

/* ─── Semântica de tarefa ──────────────────────────────────────────── */

/** Tarefa concluída = ticket em categoria resolvida/fechada. */
export function isTaskDone(task: ProjectGanttTask): boolean {
  const cat = task.ticket?.statusCategory;
  return cat === 'resolved' || cat === 'closed';
}

/** Tarefa atrasada = fim antes de hoje e ainda não concluída. */
export function isTaskOverdue(task: ProjectGanttTask): boolean {
  if (isTaskDone(task)) return false;
  const end = new Date(task.endDate);
  if (Number.isNaN(end.getTime())) return false;
  const endMs = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate());
  return endMs < todayUtcMs();
}

/** Tarefa que vence hoje (fim == dia de hoje) e não concluída. */
export function isDueToday(task: ProjectGanttTask): boolean {
  if (isTaskDone(task)) return false;
  const end = new Date(task.endDate);
  if (Number.isNaN(end.getTime())) return false;
  const endMs = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate());
  return endMs === todayUtcMs();
}

export type TicketStatusCategory = 'new' | 'in_progress' | 'waiting' | 'resolved' | 'closed';

const STATUS_CHIP: Record<TicketStatusCategory, { label: string; bg: string; fg: string }> = {
  new: { label: 'Nova', bg: 'rgba(148, 163, 184, 0.18)', fg: '#475569' },
  in_progress: { label: 'Em andamento', bg: 'rgba(139, 92, 246, 0.16)', fg: '#6d28d9' },
  waiting: { label: 'Em espera', bg: 'rgba(245, 158, 11, 0.16)', fg: '#b45309' },
  resolved: { label: 'Resolvida', bg: 'rgba(16, 185, 129, 0.16)', fg: '#047857' },
  closed: { label: 'Fechada', bg: 'rgba(100, 116, 139, 0.18)', fg: '#334155' },
};

export function statusChip(cat?: TicketStatusCategory): { label: string; bg: string; fg: string } {
  return (cat && STATUS_CHIP[cat]) || STATUS_CHIP.new;
}

/* ─── Fases ────────────────────────────────────────────────────────── */

/** Mapa `phaseId → ProjectPhase` para lookup de nome/cor. */
export function phasesById(phases: ProjectPhase[]): Map<string, ProjectPhase> {
  const map = new Map<string, ProjectPhase>();
  phases.forEach((p) => map.set(p.id, p));
  return map;
}

/* ─── Modelo do Gantt ──────────────────────────────────────────────── */

export interface GanttModel {
  tasks: TroiaGanttTask[];
  links: TroiaGanttLink[];
}

/**
 * Monta o modelo do Gantt (summary por fase + tarefas/marcos + links FS) a
 * partir do `ProjectGanttResponse`. As fases viram summary tasks colapsáveis;
 * cada tarefa referencia sua fase via `parent`. As dependências viram links
 * `e2s` (Finish-to-Start).
 */
export function buildGanttModel(gantt: ProjectGanttResponse): GanttModel {
  const phaseMap = phasesById(gantt.project.phases ?? []);
  const usedPhaseIds = new Set<string>();

  const taskNodes: TroiaGanttTask[] = gantt.tasks.map((t) => {
    const phase = t.phaseId ? phaseMap.get(t.phaseId) : undefined;
    if (t.phaseId && phase) usedPhaseIds.add(t.phaseId);
    const title = t.ticket?.title || 'Tarefa';
    const node: TroiaGanttTask = {
      id: t.id,
      text: title,
      start: isoToLocalDate(t.startDate),
      end: isoToLocalDate(t.endDate),
      progress: isTaskDone(t) ? 100 : 0,
      type: t.isMilestone ? 'milestone' : 'task',
      color: phase?.color ?? gantt.project.color,
      blocked: t.blocked,
      ticketId: t.ticket?.ticketId,
    };
    if (t.phaseId && phase) node.parent = t.phaseId;
    return node;
  });

  // Summary tasks: uma por fase que tenha ao menos uma tarefa.
  const summaryNodes: TroiaGanttTask[] = (gantt.project.phases ?? [])
    .filter((p) => usedPhaseIds.has(p.id))
    .map((p) => {
      const children = gantt.tasks.filter((t) => t.phaseId === p.id);
      const starts = children.map((c) => new Date(c.startDate).getTime()).filter((n) => !Number.isNaN(n));
      const ends = children.map((c) => new Date(c.endDate).getTime()).filter((n) => !Number.isNaN(n));
      const minStart = starts.length ? new Date(Math.min(...starts)) : new Date();
      const maxEnd = ends.length ? new Date(Math.max(...ends)) : new Date();
      return {
        id: p.id,
        text: p.name,
        start: isoToLocalDate(minStart.toISOString()),
        end: isoToLocalDate(maxEnd.toISOString()),
        progress: 0,
        type: 'summary' as const,
        color: p.color,
      };
    });

  const links: TroiaGanttLink[] = [];
  gantt.tasks.forEach((t) => {
    (t.dependencies ?? []).forEach((dep) => {
      links.push({
        id: `${dep.taskId}__${t.id}`,
        source: dep.taskId,
        target: t.id,
        type: 'e2s',
      });
    });
  });

  return { tasks: [...summaryNodes, ...taskNodes], links };
}
